import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Bank, HandCoins, Money, Receipt, Scales, Warning } from "@phosphor-icons/react";
import type { DateRangeValue } from "@/components/admin/DateRangePicker";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { throwIfNotOk } from "@/lib/api-error";
import type { PayoutsReportResponse } from "./types";
import { ErrorCard, km, LoadingState, Metric, shortDate } from "./report-shared";

const chartConfig = {
  amount: { label: "Isplaćeno (neto)", color: "var(--chart-1)" },
} satisfies ChartConfig;

export function PayoutsReportBody({ dateRange }: { dateRange: DateRangeValue }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "reports", "payouts", dateRange.from, dateRange.to],
    queryFn: async () => {
      const params = new URLSearchParams({ from: dateRange.from, to: dateRange.to });
      const res = await fetch(`/api/admin/reports/payouts?${params}`);
      await throwIfNotOk(res, "Greška pri učitavanju izvještaja o isplatama");
      return res.json() as Promise<PayoutsReportResponse>;
    },
  });

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorCard message={(error as Error).message} />;
  if (!data) return null;

  return (
    <>
      {/* Neto i bruto su dvije osnovice i obje su označene u nazivu, jer bi inače
          "isplaćeno" i "neisplaćeno" izgledali uporedivo a ne bi bili: neisplaćeno se
          vodi bruto, a na račun terapeuta ide neto (kod ugovora o djelu, nakon
          obustava). */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <Metric
          tone="primary"
          icon={Money}
          label="Isplaćeno terapeutima (neto)"
          value={km(data.totalPaidNet)}
          detail={`${data.payoutCount} isplata u periodu`}
        />
        <Metric
          icon={Scales}
          label="Bruto osnovica isplata"
          value={km(data.totalPaidGross)}
          detail="Prije obustava, uporedivo s neisplaćenim"
        />
        <Metric
          icon={Bank}
          label="Obustavljeno (porez i doprinosi)"
          value={km(data.totalWithheld)}
          detail="Ugovori o djelu, uplaćuje platforma"
        />
        <Metric
          icon={HandCoins}
          label="Broj isplata"
          value={String(data.payoutCount)}
          detail="U odabranom periodu"
        />
        <Metric
          icon={Receipt}
          label="Broj stavki"
          value={String(data.sessionCount + data.enrollmentCount)}
          detail={`Sesije: ${data.sessionCount}, grupne terapije: ${data.enrollmentCount}`}
        />
        <Metric
          icon={Warning}
          label="Trenutno neisplaćeno (bruto)"
          value={km(data.currentUnpaidGross)}
          detail="Dug prema terapeutima sada"
        />
      </div>

      <ChartContainer config={chartConfig} className="h-[320px] w-full">
        <BarChart accessibilityLayer data={data.series} margin={{ left: 4, right: 8, top: 8 }}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            minTickGap={24}
            tickFormatter={shortDate}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={64}
            tickFormatter={(value) => `${Number(value).toLocaleString("bs-BA")} KM`}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                indicator="dot"
                labelFormatter={(value) => shortDate(String(value))}
                formatter={(value) => (
                  <div className="flex min-w-36 items-center justify-between gap-4">
                    <span className="text-muted-foreground">Isplaćeno (neto)</span>
                    <span className="font-mono font-medium tabular-nums text-foreground">
                      {km(Number(value))}
                    </span>
                  </div>
                )}
              />
            }
          />
          <Bar dataKey="amount" fill="var(--color-amount)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ChartContainer>
    </>
  );
}
